export function getNestedChildren(hostElement: Element, tagName: string): HTMLCollectionOf<Element> {
  return hostElement.getElementsByTagName(tagName);
}

export function getNestedChildrenOf<T extends Element>(hostElement: Element, tagName: string): HTMLCollectionOf<T> {
  const elements = getNestedChildren(hostElement, tagName);
  return <HTMLCollectionOf<T>>(elements as unknown);
}

export function getFirstNestedChild<T>(hostElement: Element, tagName: string): T {
  const slottedElements = getNestedChildren(hostElement, tagName);
  return slottedElements.length > 0 ? ((slottedElements[0] as unknown) as T) : null;
}

/**
 * Returns a HTMLElement selected based on slot information.
 * If the child is wrapped by another div (this is a typical scenario in AEM) the selector
 * searches for the defined tag name inside of the element coming with the slot attribute.
 * @param node Node the selector is applied on
 * @param slotName string that defines the slot name for the queryselection
 * @param tag actual tag name that should be the result of the queryselection
 * @example preciseSlottedChildSelector(this.hostElement, "header-image", "EON-UI-IMAGE")
 */
export function preciseSlottedChildSelector(node: HTMLElement, slotName: string, tag: string) {
  let finalSelection: HTMLElement;
  let tempSelection = node.querySelector(`[slot="${slotName}"]`) as HTMLElement;

  if (tempSelection) {
    if (tempSelection.tagName === tag) {
      finalSelection = tempSelection;
    } else {
      finalSelection = tempSelection.querySelector(tag);
    }
  }

  return finalSelection;
}

/**
 * Returns the width of the host element's parent container
 * @param the child element whose parent element is targeted
 * @example getParentWidth(this.hostElement)
 */
export function getParentWidth(hostElement: Element): number {
  return hostElement?.parentElement ? hostElement.parentElement.offsetWidth : 0;
}

export function elementHasDimensions(hostElement: HTMLElement, callback) {
  const hasDimensions = new Promise((resolve) => {
    const checkDimension = () => {
      if (!hostElement.clientWidth) {
        requestAnimationFrame(() => checkDimension());
      } else {
        resolve("Found");
      }
    };
    checkDimension();
  });

  hasDimensions.then(() => {
    callback();
  });
}

export function getParentEonUiElement(element: HTMLElement): HTMLElement {
  const parent = (element.parentNode as HTMLElement) || ((element as any).host as HTMLElement);
  if (!parent || parent.nodeName === "HTML") {
    return null;
  }

  if (parent.tagName?.match(/^EON-UI/)) return parent;

  return getParentEonUiElement(parent);
}

export function getSpecificParentEonUiElement(element: HTMLElement, parentTagName: string): HTMLElement {
  const parent = (element.parentNode as HTMLElement) || ((element as any).host as HTMLElement);
  var regularExpression = new RegExp(`${parentTagName.toUpperCase()}$`, "g");

  if (!parent || parent.nodeName === "HTML") {
    return null;
  }

  if (parent.tagName.match(regularExpression)) return parent;

  return getSpecificParentEonUiElement(parent, parentTagName);
}

/**
 * This selector returns all eon ui elements inside the host elements dom,
 * but remove the eon ui elements from the set, that are encapsulated by other eon-ui-elements.
 * @param hostElement
 * @returns HTMLElement[]
 */
export function getOnlyClosestEonUiChildren(hostElement: Element): HTMLElement[] {
  const children = Array.from(hostElement.children);
  let eonUiChildArray = [];

  children.forEach((child) => {
    if (child.tagName.match(/^EON-UI/)) {
      eonUiChildArray.push(child);
    } else {
      eonUiChildArray = eonUiChildArray.concat(getOnlyClosestEonUiChildren(child));
    }
  });

  return eonUiChildArray;
}

export function getWebsiteNavigation(): any {
  let navigation;

  if (document.getElementsByTagName("eon-ui-navigation")[0]) {
    navigation = document.getElementsByTagName("eon-ui-navigation")[0];
  } else if (document.getElementsByTagName("eon-ui-website-navigation")[0]) {
    navigation = document.getElementsByTagName("eon-ui-website-navigation")[0];
  }

  return navigation;
}

export function getAllEonUiChildren(hostElement: Element): Element[] {
  const matchingElements: Element[] = [];

  const children = hostElement.querySelectorAll("*");

  children.forEach((child: Element) => {
    if (child.tagName.match(/^EON-UI/i)) {
      matchingElements.push(child);
    }
  });

  return matchingElements;
}

export function closestElement(
  selector, // selector like in .closest()
  base = this, // extra functionality to skip a parent
  __Closest = (el, found = el && el.closest(selector)) =>
    !el || el === document || el === window
      ? null // standard .closest() returns null for non-found selectors also
      : found
      ? found // found a selector INside this element
      : __Closest(el.getRootNode().host) // recursion!! break out to parent DOM
) {
  return __Closest(base);
}
